import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { getRuntimeConfig } from '../config/runtimeConfig';
import ActionPromptModal from '../components/ActionPromptModal';

export default function AdminReports() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [rows, setRows] = useState([]);
  const [closingId, setClosingId] = useState('');
  const [actionId, setActionId] = useState('');
  const [openOnly, setOpenOnly] = useState(true);
  const [quarantinedOnly, setQuarantinedOnly] = useState(false);
  const [kindFilter, setKindFilter] = useState('all');
  const [promptState, setPromptState] = useState({
    open: false,
    title: '',
    description: '',
    inputType: 'text',
    value: '',
    submitLabel: 'Confirm',
    loading: false,
    onConfirm: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [{ data: authData }, cfg] = await Promise.all([
        supabase.auth.getUser(),
        getRuntimeConfig(),
      ]);
      const authUserId = authData?.user?.id || null;
      if (!authUserId) {
        setIsAdmin(false);
        setRows([]);
        setError('Sign in required.');
        return;
      }

      const runtimeAdmins = new Set((cfg?.adminAuthUserIds || []).map((v) => String(v)));
      const runtimeAllowed = runtimeAdmins.has(String(authUserId));
      const { data: adminRow } = await supabase
        .from('moderation_admins')
        .select('auth_user_id')
        .eq('auth_user_id', authUserId)
        .eq('is_active', true)
        .maybeSingle();
      const allowed = runtimeAllowed || Boolean(adminRow?.auth_user_id);
      setIsAdmin(allowed);
      if (!allowed) {
        setRows([]);
        setError('Admin access required.');
        return;
      }

      const { data, error: rpcErr } = await supabase.rpc('get_moderation_reports', { p_limit: 200 });
      if (rpcErr) throw rpcErr;
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('[admin-reports] load_failed', e);
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openPrompt = useCallback((config) => {
    setPromptState({
      open: true,
      title: config.title || 'Confirm',
      description: config.description || '',
      inputType: config.inputType || 'text',
      value: config.value ?? '',
      submitLabel: config.submitLabel || 'Confirm',
      loading: false,
      onConfirm: config.onConfirm || null,
    });
  }, []);

  const closePrompt = useCallback(() => {
    setPromptState((prev) => ({ ...prev, open: false, loading: false, onConfirm: null }));
  }, []);

  const submitPrompt = useCallback(async () => {
    if (!promptState.onConfirm) return closePrompt();
    setPromptState((prev) => ({ ...prev, loading: true }));
    try {
      await promptState.onConfirm(promptState.value);
      closePrompt();
    } catch (e) {
      setError(e?.message || String(e));
      setPromptState((prev) => ({ ...prev, loading: false }));
    }
  }, [promptState, closePrompt]);

  const closeReport = useCallback(async (row, action = 'close') => {
    const reportId = row?.report_id;
    if (!reportId) return;
    openPrompt({
      title: 'Close Report',
      description: 'Add a resolution note (optional).',
      inputType: 'textarea',
      value: '',
      submitLabel: 'Close Report',
      onConfirm: async (promptValue) => {
        const closeNote = (promptValue || '').trim() || null;
        setClosingId(reportId);
        try {
          let rpcRes;
          if (row?.report_kind === 'daily') {
            rpcRes = await supabase.rpc('admin_close_daily_report', {
              p_report_id: reportId,
              p_close_note: closeNote,
            });
          } else {
            rpcRes = await supabase.rpc('admin_close_trial_report', {
              p_report_id: reportId,
              p_action: action,
              p_close_note: closeNote,
            });
          }
          const { data, error: rpcErr } = rpcRes;
          if (rpcErr) throw rpcErr;
          if (!data?.ok) throw new Error(data?.error || 'close_failed');
          setSuccessMsg(`Report closed${row?.report_kind === 'trial' ? ` (${action})` : ''}.`);
          await load();
        } catch (e) {
          console.error('[admin-reports] close_failed', e);
          throw e;
        } finally {
          setClosingId('');
        }
      },
    });
  }, [load, openPrompt]);

  const approveTrial = useCallback(async (row) => {
    if (!row?.trial_id) return;
    openPrompt({
      title: 'Approve Trial',
      description: 'Add an approval note (optional).',
      inputType: 'textarea',
      value: '',
      submitLabel: 'Approve',
      onConfirm: async (promptValue) => {
        const note = (promptValue || '').trim() || null;
        setActionId(row.report_id || row.trial_id);
        setError('');
        try {
          const rpc = await supabase.rpc('admin_unquarantine_trial', {
            p_trial_id: row.trial_id,
            p_note: note,
          });
          if (rpc.error) throw rpc.error;
          if (!rpc.data?.ok) throw new Error(rpc.data?.error || 'approve_failed');
          setSuccessMsg('Trial approved (unquarantined).');
          await load();
        } catch (e) {
          console.error('[admin-reports] approve_failed', e);
          throw e;
        } finally {
          setActionId('');
        }
      },
    });
  }, [load, openPrompt]);

  const toggleTrialBlocked = useCallback(async (row, shouldBlock) => {
    if (!row?.trial_id) return;
    const startBlockToggle = async (reasonValue) => {
      setActionId(row.report_id || row.trial_id);
      setError('');
      try {
        const reason = shouldBlock ? ((reasonValue || '').trim() || 'admin_block') : null;
        const rpc = shouldBlock
          ? await supabase.rpc('admin_block_trial', { p_trial_id: row.trial_id, p_reason: reason })
          : await supabase.rpc('admin_unblock_trial', { p_trial_id: row.trial_id });
        if (rpc.error) throw rpc.error;
        if (!rpc.data?.ok) throw new Error(rpc.data?.error || 'toggle_block_failed');
        setSuccessMsg(shouldBlock ? 'Trial blocked.' : 'Trial unblocked.');
        await load();
      } catch (e) {
        console.error('[admin-reports] toggle_block_failed', e);
        throw e;
      } finally {
        setActionId('');
      }
    };

    if (!shouldBlock) {
      startBlockToggle(null).catch((e) => setError(e?.message || String(e)));
      return;
    }

    openPrompt({
      title: 'Block Trial',
      description: 'Add a block reason (optional).',
      inputType: 'text',
      value: 'admin_block',
      submitLabel: 'Block Trial',
      onConfirm: startBlockToggle,
    });
  }, [load, openPrompt]);

  const openReports = useMemo(() => rows.filter((r) => String(r.status || '').toLowerCase() === 'open'), [rows]);
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (openOnly && String(r.status || '').toLowerCase() !== 'open') return false;
      if (kindFilter !== 'all' && String(r.report_kind || '') !== kindFilter) return false;
      if (quarantinedOnly && !(r.report_kind === 'trial' && r.trial_is_quarantined)) return false;
      return true;
    });
  }, [rows, openOnly, kindFilter, quarantinedOnly]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl sm:text-2xl font-black">Admin Reports</h1>
          <Link to="/" className="text-sm text-slate-300 hover:text-slate-100">Back</Link>
        </div>

        {error && <div className="text-sm rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2">{error}</div>}
        {successMsg && <div className="text-sm rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">{successMsg}</div>}
        {loading && <div className="text-sm text-slate-400">Loading...</div>}

        {!loading && isAdmin && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-slate-300">Open reports: {openReports.length} • Total: {rows.length}</div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <label className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1">
                  <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
                  <span>Open only</span>
                </label>
                <label className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1">
                  <input type="checkbox" checked={quarantinedOnly} onChange={(e) => setQuarantinedOnly(e.target.checked)} />
                  <span>Quarantined only</span>
                </label>
                <select
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value)}
                  className="rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-xs"
                >
                  <option value="all">All reports</option>
                  <option value="trial">Trial reports</option>
                  <option value="daily">Daily reports</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              {filteredRows.map((row) => (
                <div key={row.report_id} className="rounded-xl border border-slate-700 bg-slate-900/70 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">
                        <span className="mr-2 text-[10px] uppercase tracking-wide text-slate-400">{row.report_kind || 'trial'}</span>
                        {row.item_title || row.trial_title || 'Untitled item'}
                      </div>
                      <div className="text-xs text-slate-400">
                        {row.created_at} • {row.reason} • status: {row.status}
                        {row.report_kind === 'trial'
                          ? ` • blocked: ${row.trial_is_blocked ? 'yes' : 'no'} • quarantined: ${row.trial_is_quarantined ? 'yes' : 'no'} • 24h reports: ${row.report_count_24h ?? 0}`
                          : ''}
                      </div>
                      {row.report_kind === 'trial' && (row.trial_quarantine_reason || row.trial_quarantined_at) && (
                        <div className="text-xs text-amber-300 mt-1">
                          quarantine reason: {row.trial_quarantine_reason || '—'} • quarantined_at: {row.trial_quarantined_at || '—'}
                        </div>
                      )}
                      <div className="text-xs text-slate-300 mt-1">reporter: {row.reporter_username || row.reporter_user_id}</div>
                      {row.note && <div className="text-xs text-slate-200 mt-2">note: {row.note}</div>}
                      {row.resolution_note && <div className="text-xs text-emerald-200 mt-1">resolution: {row.resolution_note}</div>}
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        disabled={closingId === row.report_id || String(row.status) === 'closed'}
                        onClick={() => closeReport(row, 'close')}
                        className="rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold"
                      >
                        Close
                      </button>
                      {row.report_kind === 'trial' && (
                        <>
                          <button
                            disabled={closingId === row.report_id}
                            onClick={() => closeReport(row, 'block')}
                            className="rounded-lg bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold"
                          >
                            Close + Block Trial
                          </button>
                          <button
                            disabled={closingId === row.report_id}
                            onClick={() => closeReport(row, 'approve')}
                            className="rounded-lg bg-cyan-700 hover:bg-cyan-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold"
                          >
                            Close + Approve
                          </button>
                          <button
                            disabled={actionId === (row.report_id || row.trial_id)}
                            onClick={() => toggleTrialBlocked(row, !row.trial_is_blocked)}
                            className="rounded-lg bg-amber-700 hover:bg-amber-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold"
                          >
                            {row.trial_is_blocked ? 'Unblock Trial' : 'Block Trial'}
                          </button>
                          <button
                            disabled={actionId === (row.report_id || row.trial_id)}
                            onClick={() => approveTrial(row)}
                            className="rounded-lg bg-sky-700 hover:bg-sky-600 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold"
                          >
                            Approve (Unquarantine)
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {filteredRows.length === 0 && <div className="text-sm text-slate-400">No reports match current filters.</div>}
            </div>
          </>
        )}
      </div>
      <ActionPromptModal
        open={promptState.open}
        title={promptState.title}
        description={promptState.description}
        inputType={promptState.inputType}
        value={promptState.value}
        submitLabel={promptState.submitLabel}
        loading={promptState.loading}
        onChange={(value) => setPromptState((prev) => ({ ...prev, value }))}
        onCancel={closePrompt}
        onSubmit={submitPrompt}
      />
    </div>
  );
}
